import React, { useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

interface SubBarProps {
  /** Accessible name, e.g. "Insert". */
  label: string;
  onClose: () => void;
  /**
   * Pointer events inside this element don't dismiss the sub-bar. Point it at the
   * wrapper holding both bars, so clicking a trigger toggles rather than
   * close-then-reopen.
   */
  boundaryRef: React.RefObject<HTMLElement | null>;
  /** Focus returns here on Escape — normally the trigger that opened the bar. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * `inline` renders the section unadorned (no own capsule) so it can sit inside
   * the persistent bar, expanding it from within instead of floating above.
   */
  inline?: boolean;
  children: React.ReactNode;
}

const FOCUSABLE = 'button:not([disabled])';

/**
 * A transient toolbar that slides in above the persistent bar. Owns its own
 * dismissal (Escape, outside pointer-down) and roving arrow-key focus, so the
 * editor only has to say which sub-bar is open.
 */
export function SubBar({ label, onClose, boundaryRef, returnFocusRef, inline = false, children }: SubBarProps) {
  const barRef = useRef<HTMLDivElement>(null);

  const dismiss = useCallback(() => {
    returnFocusRef?.current?.focus();
    onClose();
  }, [onClose, returnFocusRef]);

  // Focus the first tool so the bar is immediately keyboard-operable.
  useEffect(() => {
    barRef.current?.querySelector<HTMLButtonElement>(FOCUSABLE)?.focus();
  }, []);

  // Dismiss on a pointer-down anywhere outside the bars.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!boundaryRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [boundaryRef, onClose]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation(); // don't let the editor's global Escape also deselect
      dismiss();
      return;
    }
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;

    const items = Array.from(barRef.current?.querySelectorAll<HTMLButtonElement>(FOCUSABLE) ?? []);
    if (!items.length) return;
    e.preventDefault();

    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'Home' ? 0
        : e.key === 'End' ? items.length - 1
          : e.key === 'ArrowRight' ? (i + 1) % items.length
            : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  if (inline) {
    // The section grows the pill from within: width springs open (interruptible
    // — retargets mid-animation if the user flips sections quickly), items
    // cascade in with a short stagger, and the exit is faster than the enter.
    return (
      <motion.div
        ref={barRef}
        role="group"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="flex items-center gap-0.5 overflow-hidden border-l border-border pl-1.5 ml-0.5"
        initial={{ width: 0, opacity: 0 }}
        animate={{
          width: 'auto',
          opacity: 1,
          transition: { type: 'spring', duration: 0.4, bounce: 0.12, opacity: { duration: 0.12 } },
        }}
        exit={{ width: 0, opacity: 0, transition: { duration: 0.15, ease: 'easeOut' } }}
      >
        {React.Children.map(children, (child, i) => (
          <motion.div
            key={i}
            className="flex flex-none items-center gap-0.5"
            initial={{ opacity: 0, x: -4 }}
            animate={{
              opacity: 1,
              x: 0,
              transition: { type: 'spring', duration: 0.3, bounce: 0, delay: 0.08 + i * 0.03 },
            }}
            exit={{ opacity: 0, transition: { duration: 0.06 } }}
          >
            {child}
          </motion.div>
        ))}
      </motion.div>
    );
  }

  return (
    <div
      ref={barRef}
      role="toolbar"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="flex gap-0.5 max-w-[calc(100vw-1rem)] overflow-x-auto border p-1 rounded-full shadow-xl bg-surface-secondary border-border animate-in fade-in-0 slide-in-from-bottom-2 duration-150 motion-reduce:animate-none"
    >
      {children}
    </div>
  );
}
